import { gsap } from "gsap";
import { useEffect, useRef, useState } from "react";
import {
  ASSETS,
  TOTAL_BOSSES,
  HEALTH_BAR_STAGES,
  JUMP_DURATION,
  getDynamicConstants,
} from "./animation.config";

export const useHeroAnimation = () => {
  // --- STATE ---
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true); // Muted by default for better UX

  // --- REFS ---
  const timelineRef = useRef(null);
  const refs = {
    container: useRef(null),
    sonic: useRef(null),
    healthBars: useRef([]),
    statuses: useRef([]),
    bossContainers: useRef([]),
    banners: useRef([]),
    bossStats: useRef([]),
    winnerMessage: useRef(null),
    startPortal: useRef(null),
    endPortal: useRef(null),
    controls: useRef(null),
    particleContainer: useRef(null), // Dedicated container for particles
    screenFlash: useRef(null),
    // Refs for elements created dynamically, to ensure proper cleanup
    dynamicElements: useRef({
        trails: [],
        particles: [],
        portalGlows: [],
    }),
  };

  const audioRefs = useRef({
    jump: typeof Audio !== "undefined" ? new Audio("/assets/sounds/jump.mp3") : null,
    hit: typeof Audio !== "undefined" ? new Audio("/assets/sounds/hit.mp3") : null,
    victory: typeof Audio !== "undefined" ? new Audio("/assets/sounds/victory.mp3") : null,
    portal: typeof Audio !== "undefined" ? new Audio("/assets/sounds/portal.mp3") : null,
    bossDefeat: typeof Audio !== "undefined" ? new Audio("/assets/sounds/boss_defeat.mp3") : null,
  });

  // --- CONTROL FUNCTIONS ---
  const playSound = (sound, volume = 0.7) => {
    if (!isMuted && audioRefs.current[sound]) {
      audioRefs.current[sound].currentTime = 0;
      audioRefs.current[sound].volume = volume;
      audioRefs.current[sound].play().catch((e) => console.log("Audio play failed:", e));
    }
  };
  
  const togglePlayPause = () => timelineRef.current?.paused(!timelineRef.current.paused());
  
  useEffect(() => {
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    gsap.ticker.add(() => {
      if(timelineRef.current) {
        if(timelineRef.current.isTweening()) { // More reliable check
          timelineRef.current.paused() ? onPause() : onPlay();
        }
      }
    });
  }, []);

  const toggleMute = () => setIsMuted(!isMuted);

  const cleanupDynamicElements = () => {
    Object.values(refs.dynamicElements.current).forEach(arr => {
        arr.forEach(el => {
            gsap.killTweensOf(el);
            el.remove();
        });
        arr.length = 0;
    });
  };

  const resetAnimation = () => {
    if (timelineRef.current) {
      timelineRef.current.pause();
      
      // Kill all tweens on tracked elements
      const elementsToReset = [
        ...refs.bossStats.current,
        ...refs.banners.current,
        refs.winnerMessage.current,
        refs.sonic.current,
      ].filter(Boolean);
      gsap.killTweensOf(elementsToReset);

      // Reset static elements to initial state
      refs.statuses.current.forEach((status) => status && (status.style.display = "none"));
      refs.healthBars.current.forEach((bar, i) => bar && i > 0 && (bar.src = ASSETS.healthBarBase));
      refs.bossStats.current.forEach(stats => stats && gsap.set(stats, { autoAlpha: 0 }));
      gsap.set(refs.winnerMessage.current, { autoAlpha: 0, scale: 0.8, y: 20 });
      gsap.set(refs.banners.current, { filter: "brightness(1) saturate(1)", scale: 1 });
      
      cleanupDynamicElements();
      
      // Restart the timeline after a short delay to ensure DOM is clean
      setTimeout(() => {
        timelineRef.current?.restart();
      }, 50);
    }
  };

  // --- MAIN EFFECT ---
  useEffect(() => {
    const createAnimation = () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
        cleanupDynamicElements();
      }

      const {
        sonic, startPortal, endPortal, winnerMessage, particleContainer, screenFlash,
        healthBars, bossContainers, banners, bossStats: bossStatsRefs, statuses
      } = refs;

      if (!sonic.current || !startPortal.current || !endPortal.current || !winnerMessage.current) return;

      const { JUMP_HEIGHT } = getDynamicConstants();
      
      // --- Particle & Effect Creators (more robust and reusable) ---
      const createParticles = (config) => {
        if (!particleContainer.current) return;
        for (let i = 0; i < config.count; i++) {
          const p = document.createElement("div");
          p.className = "absolute rounded-full pointer-events-none";
          p.style.backgroundColor = config.colors[Math.floor(Math.random() * config.colors.length)];
          p.style.width = `${Math.random() * (config.size?.[1] ?? 5) + (config.size?.[0] ?? 2)}px`;
          p.style.height = p.style.width;
          particleContainer.current.appendChild(p);
          refs.dynamicElements.current.particles.push(p);

          gsap.fromTo(p, 
            { x: config.x, y: config.y, scale: 1, autoAlpha: 1 }, 
            {
              x: `+=${(Math.random() - 0.5) * config.spread}`,
              y: `+=${(Math.random() - 0.5) * config.spread - (config.lift ?? 0)}`,
              scale: 0,
              autoAlpha: 0,
              duration: Math.random() * (config.duration?.[1] ?? 1.5) + (config.duration?.[0] ?? 0.5),
              ease: "power2.out",
              onComplete: () => p.remove(),
            }
          );
        }
      };
      
      // --- Initial Setup ---
      gsap.set([startPortal.current, endPortal.current], { scale: 1, autoAlpha: 1 });
      gsap.to([startPortal.current.querySelector('.portal-ring'), endPortal.current.querySelector('.portal-ring')], {
        rotation: 360, duration: 8, repeat: -1, ease: 'none'
      });
      gsap.to([startPortal.current, endPortal.current], {
        scale: 1.05, duration: 2.5, repeat: -1, yoyo: true, ease: "sine.inOut"
      });
      
      timelineRef.current = gsap.timeline({
        repeat: -1,
        repeatDelay: 2,
        onRepeat: () => { // Reset state on each loop
          gsap.set(statuses.current, { display: 'none' });
          healthBars.current.forEach((bar, i) => bar && i > 0 && (bar.src = ASSETS.healthBarBase));
          gsap.set(bossStatsRefs.current, { autoAlpha: 0 });
          gsap.set(winnerMessage.current, { autoAlpha: 0, scale: 0.8, y: 20 });
        }
      });
      const tl = timelineRef.current;
      const sonicStartRect = sonic.current.getBoundingClientRect();

      // --- Animation Sequence ---
      tl.addLabel("start");
      tl.to(startPortal.current, { scale: 1.5, autoAlpha: 0.7, duration: 0.4, ease: "power2.out", onStart: () => playSound("portal", 0.5) }, "start");
      tl.fromTo(sonic.current, 
        { scale: 0, autoAlpha: 0, y: 20 }, 
        { scale: 1, autoAlpha: 1, y: 0, duration: 0.6, ease: "elastic.out(1, 0.5)" }
      , "start+=0.2");

      for (let bossIndex = TOTAL_BOSSES; bossIndex >= 1; bossIndex--) {
        const healthBar = healthBars.current[bossIndex];
        const bossContainer = bossContainers.current[bossIndex];
        const banner = banners.current[bossIndex];
        const bossStats = bossStatsRefs.current[bossIndex];
        if (!healthBar || !bossContainer || !banner) continue;

        const healthBarRect = healthBar.getBoundingClientRect();
        const targetX = healthBarRect.left + healthBarRect.width / 2 - (sonicStartRect.left + sonicStartRect.width / 2);
        const targetY = healthBarRect.top - sonicStartRect.top - sonicStartRect.height;
        const label = `boss${bossIndex}`;

        tl.addLabel(label);
        tl.to(sonic.current, { x: targetX, y: targetY + JUMP_HEIGHT * 1.5, duration: 0.3, ease: "power1.in", onStart: () => playSound("jump", 0.4) }, label);
        tl.to(sonic.current, { 
          y: targetY, duration: 0.3, ease: "bounce.out", 
          onStart: () => {
            playSound("hit", 0.6);
            // Squash and stretch on impact
            gsap.fromTo(sonic.current, {scaleY: 0.8, scaleX: 1.2}, {scaleY: 1, scaleX: 1, duration: 0.5, ease: 'elastic.out(1, 0.5)'});
            gsap.to(bossContainer, { y: 5, duration: 0.1, yoyo: true, repeat: 1 });
          }
        }, `${label}+=0.3`);
        
        for (let jumpNum = 0; jumpNum < 4; jumpNum++) {
          tl.to(sonic.current, { y: `-=${JUMP_HEIGHT}`, duration: JUMP_DURATION, ease: "power1.out", onStart: () => {
            playSound("jump", 0.3);
            gsap.to(sonic.current, { rotation: "+=360", duration: JUMP_DURATION * 2, ease: "none" });
          }}, `>+${jumpNum === 0 ? 0.1 : 0}`);

          tl.to(sonic.current, { y: targetY - 3, duration: JUMP_DURATION, ease: "power1.in", onStart: () => {
            playSound("hit", 0.8);
            healthBar.src = ASSETS.healthBarHit[jumpNum % HEALTH_BAR_STAGES];
            gsap.to(bossContainer, { x: (Math.random() - 0.5) * 8, duration: 0.1, yoyo: true, repeat: 1 });
            // Boss defeated
            if (jumpNum === 3) {
              playSound("bossDefeat", 0.7);
              const statusIcon = statuses.current[bossIndex];
              if (statusIcon) {
                statusIcon.style.display = "block";
                gsap.fromTo(statusIcon, { scale: 0.5, autoAlpha: 0 }, { scale: 1.2, autoAlpha: 1, duration: 0.4, ease: "back.out(1.7)", yoyo: true, repeat: 1 });
              }
              if (bossStats) gsap.to(bossStats, { autoAlpha: 1, y: -10, duration: 0.5, ease: "power2.out" });
              gsap.to(banner, { keyframes: [{ filter: "brightness(1.8) saturate(1.5)", scale: 1.05, duration: 0.1 }, { filter: "brightness(1) saturate(1)", scale: 1, duration: 0.5 }] });
              createParticles({ x: healthBarRect.left, y: healthBarRect.top, count: 20, colors: ['#ffcc00', '#ff9900', '#ffffff'], spread: 80, lift: 20 });
            }
          }});
        }
      }

      // --- Victory Sequence ---
      const winnerPlatform = bossContainers.current[0];
      if (winnerPlatform) {
        const winnerRect = winnerPlatform.getBoundingClientRect();
        const targetWinnerX = winnerRect.left + winnerRect.width / 2 - (sonicStartRect.left + sonicStartRect.width / 2);
        const targetWinnerY = winnerRect.top - sonicStartRect.top - sonicStartRect.height / 2;
        
        tl.addLabel("victory");
        tl.to(sonic.current, { x: targetWinnerX, y: targetWinnerY - JUMP_HEIGHT * 2, scale: 1.2, duration: 0.7, ease: "power2.inOut", onStart: () => playSound("jump") }, "victory");
        tl.to(sonic.current, { y: targetWinnerY, scale: 1, duration: 0.5, ease: "bounce.out", onComplete: () => {
          playSound("victory");
          gsap.to(winnerMessage.current, { autoAlpha: 1, scale: 1, y: 0, duration: 0.6, ease: "elastic.out(1, 0.5)" });
          gsap.to(endPortal.current, { scale: 1.6, autoAlpha: 0.9, duration: 0.4 });
          gsap.fromTo(screenFlash.current, { autoAlpha: 0.5 }, { autoAlpha: 0, duration: 0.8, ease: "power2.out" });
          createParticles({ x: winnerRect.left + winnerRect.width / 2, y: winnerRect.top, count: 100, colors: ['#ffcc00', '#22d3ee', '#f472b6', '#ffffff'], spread: 400, duration: [2, 4] });
        }}, ">");
        
        tl.to(sonic.current, { y: `+=${JUMP_HEIGHT / 2}`, duration: 0.3, ease: "power1.out", repeat: 3, yoyo: true }, ">");
        tl.to(sonic.current, { scale: 0, autoAlpha: 0, rotation: 720, duration: 0.6, ease: "power2.in", onStart: () => {
          playSound("portal");
          gsap.to(endPortal.current, { scale: 0, autoAlpha: 0, duration: 0.7, ease: "power2.in" });
        }}, ">");
      }
    };

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(createAnimation, 300);
    };
    const initTimeout = setTimeout(createAnimation, 200);
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(initTimeout);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      timelineRef.current?.kill();
      cleanupDynamicElements();
    };
  }, [isMuted]); // Re-run effect if isMuted changes to ensure playSound has the correct state

  return {
    refs,
    state: { isPlaying, isMuted },
    controls: { togglePlayPause, toggleMute, resetAnimation },
  };
};